// One place that knows how to talk to the server: the error shape, the cookie
// handling, and the fact that a 401 means "start again at the login screen".
import type { Block } from './components/Blocks'

export type ApiErrorBody = {
  code: string
  message: string
  fields?: Record<string, string>
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: Record<string, string>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.code)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.fields = body.fields ?? {}
  }

  /** True when the caller is not signed in, or their session has expired. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /**
   * True when the session ended because the learner signed in on another
   * device: the platform keeps a learner to one session at a time, and the
   * newer sign-in ended this one. A 401 like any other ended session, with a
   * reason the sign-in page can give.
   */
  get isSessionReplaced() {
    return this.status === 401 && this.code === 'session_replaced'
  }

  /** True when the server refused because a lesson is shut; `reason` says why. */
  get blockedReason(): string | undefined {
    return this.status === 403 ? this.fields['blocked_reason'] : undefined
  }

  /**
   * True when the learner is signed in with a password somebody else chose for
   * them, and the server will do nothing else until they choose their own.
   */
  get isPasswordChangeRequired() {
    return this.status === 403 && this.code === 'password_change_required'
  }
}

type Options = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Suppresses the global "you have been signed out" handling for this call. */
  quiet?: boolean
}

let onUnauthenticated: ((err: ApiError) => void) | null = null
let onPasswordChangeRequired: (() => void) | null = null
let onSessionReplaced: (() => void) | null = null

/**
 * Registered once by the auth provider so any call can bounce to the login
 * screen. It is handed the refusal, which says why the session ended — a
 * sign-in on another device (`isSessionReplaced`) is worth telling the
 * learner about.
 */
export function handleSignedOut(fn: (err: ApiError) => void) {
  onUnauthenticated = fn
}

/**
 * Registered once by the auth provider. A refusal with
 * `password_change_required` means the learner still holds a password somebody
 * else chose — reset by staff while this tab was open, say — and the provider
 * asks /auth/me again, which puts the "choose your own password" page in front
 * of everything else.
 */
export function handlePasswordChangeRequired(fn: () => void) {
  onPasswordChangeRequired = fn
}

/**
 * Registered once by the auth provider, which records the reason the sign-in
 * page gives. Unlike the signed-out handling it hears every call, quiet ones
 * included: a quiet call keeps its 401 to itself, but the news that the
 * learner signed in on another device must not be lost with it — a server
 * that clears the cookie with that answer says it only once, and the call
 * that hears it may be one whose failure nobody shows (an upload, the
 * language saved to the account).
 */
export function handleSessionReplaced(fn: () => void) {
  onSessionReplaced = fn
}

/** Hands a refusal to whichever of the handlers above it concerns. */
function notice(err: ApiError, quiet?: boolean) {
  if (err.isSessionReplaced) onSessionReplaced?.()
  if (err.isUnauthenticated && !quiet) onUnauthenticated?.(err)
  else if (err.isPasswordChangeRequired) onPasswordChangeRequired?.()
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? 'GET',
    // The session lives in a cookie, so every call must send it. Same-origin in
    // production; the dev server proxies /api so it is same-origin there too.
    // Only ever same-origin: the server refuses a change a browser says came
    // from another site (403 cross_origin), so there is no other kind to make.
    credentials: 'same-origin',
    // A body is JSON and says so. The server refuses any other type where it
    // reads JSON (415 unsupported_media_type): a page on another site can
    // make a browser post text or a form without asking, but not JSON. Files
    // go as multipart, by the upload helpers, never through here.
    headers: opts.body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      // A non-JSON body from a proxy or gateway; report it as an error rather
      // than letting a parse exception surface as something unrelated.
      throw new ApiError(res.status, { code: 'bad_response', message: text.slice(0, 200) })
    }
  }

  if (!res.ok) {
    const body = (parsed as { error?: ApiErrorBody } | null)?.error ?? {
      code: 'unknown',
      message: `request failed with ${res.status}`, // i18n-ignore: a log line; messageOf shows err_unknown
    }
    const err = new ApiError(res.status, body)
    notice(err, opts.quiet)
    throw err
  }

  return parsed as T
}

export const get = <T,>(path: string, signal?: AbortSignal) => api<T>(path, { signal })
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body })
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body })
export const patch = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body })
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' })

// ------------------------------------------------------------------ types --

export type User = {
  id: string
  phone: string
  full_name: string
  avatar_path?: string
  locale: string
}

/**
 * Who is signed in, as GET /auth/me and POST /auth/login answer: the learner,
 * and whether the password they signed in with is one somebody else chose —
 * staff when they were enrolled or reset, the operator from the command line.
 * While it is, the server refuses everything but choosing their own (403
 * password_change_required), and the site shows nothing but the page that
 * does it.
 *
 * Optional because a server from before the flag existed does not send it,
 * and for such a server the answer is "no".
 */
export type Identity = {
  user: User
  must_change_password?: boolean
}

export type PublicPlatform = {
  name: string
  locale: string
  colors: {
    primary?: string
    success?: string
    warning?: string
    error?: string
    // primary here is that theme's own accent; without one the theme uses
    // colors.primary (and the dark theme a lighter shade of it).
    light?: { background?: string; text?: string; footer?: string; primary?: string }
    dark?: { background?: string; text?: string; footer?: string; primary?: string }
  }
  logo_path?: string
  favicon_path?: string
}

export type BlockedReason = '' | 'progress' | 'homework' | 'schedule' | 'access' | 'unpublished'

export type LectureNode = {
  id: string
  section_id: string
  title: string
  seq: number
  completed: boolean
  progress: number
  blocked: boolean
  blocked_reason?: BlockedReason
}

export type SectionNode = {
  id: string
  title: string
  seq: number
  access: 'open' | 'closed' | 'time_controlled'
  access_start_at?: string
  image_path?: string
  /** The module's picture, an uploaded file served at /files/{id}. */
  image_file_id?: string
  lectures: LectureNode[]
  completed: number
  total: number
  blocked: boolean
  blocked_reason?: BlockedReason
}

export type CourseTree = {
  course: {
    id: string
    title: string
    description: string
    cover_path?: string
    /** The course cover, an uploaded file served at /files/{id}. */
    cover_file_id?: string
    gamification_enabled: boolean
  }
  sections: SectionNode[]
  percent: number
}

export type ProgressReport = {
  percent: number
  watched_seconds: number
  completed: boolean
  /** How far the learner may seek. Beyond this the server refuses to credit. */
  frontier: number
  position: number
}

export type Playback = {
  url: string
  duration_seconds: number
  expires_at: string
}

// ------------------------------------------------------------------ upload --

/** One stored file, as the server records it. */
export type StoredFile = {
  id: string
  filename: string
  mime: string
  size_bytes: number
}

/**
 * Sends one file as multipart. It bypasses `api()` because that helper sets a
 * JSON content type and serialises the body — here the browser has to write the
 * multipart boundary itself, which it only does when Content-Type is left alone.
 */
export async function upload(path: string, file: File, signal?: AbortSignal): Promise<StoredFile> {
  const form = new FormData()
  form.append('file', file)

  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
    signal,
  })

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      throw new ApiError(res.status, { code: 'bad_response', message: text.slice(0, 200) })
    }
  }
  if (!res.ok) {
    const body = (parsed as { error?: ApiErrorBody } | null)?.error ?? {
      code: 'unknown',
      message: `upload failed with ${res.status}`, // i18n-ignore: a log line; messageOf shows err_unknown
    }
    const err = new ApiError(res.status, body)
    // Quiet about a 401 as it always was here: the homework form that uploads
    // says so itself. A forced password change is everybody's business.
    notice(err, true)
    throw err
  }
  return parsed as StoredFile
}

export type SubmissionStatus = 'pending' | 'graded' | 'rejected'

export type SubmissionFile = {
  file_id: string
  filename: string
  size_bytes?: number
}

export type Submission = {
  id: string
  lecture_id: string
  body: string
  files: SubmissionFile[]
  status: SubmissionStatus
  grade?: number
  review_comment: string
  reviewed_at?: string
  created_at: string
}

export type Homework = {
  id: string
  instructions: string
  /** The brief's blocks (TZ2), resolved like lesson blocks; absent from an older server. */
  blocks?: Block[]
  max_files: number
  required: boolean
  acceptance_required: boolean
  submissions: Submission[]
}
